// SINGLE SOURCE OF TRUTH for all fixed, visitor-facing website content.
// Every hard-coded string / image / alt / caption that used to live inside a page
// component now lives here as a field default. The public site reads this through
// ContentContext (defaults + saved admin overrides). The admin "Site Content" tab
// is generated entirely from this schema. Do not read these strings directly in pages.
//
// Field types: text | textarea | rich | image | link | list
//   - list fields carry `item` (array of sub-field defs) and `def` (array of default rows)
//   - `fixed: true` on a list hides add/remove (rows are bound to fixed icons/layout)

const BE = process.env.REACT_APP_BACKEND_URL;
const HERO = `${BE}/api/files/44552e1f-9e21-4c95-a2e0-e29f9f527b60?w=2000`;
const CUSTOM_IMG = "https://images.pexels.com/photos/6620992/pexels-photo-6620992.jpeg?auto=compress&cs=tinysrgb&w=1400";
const PORTRAIT = "https://images.pexels.com/photos/32189248/pexels-photo-32189248.jpeg?auto=compress&cs=tinysrgb&w=1400";

export const CONTENT_SCHEMA = [
  {
    key: "home", label: "Home",
    sections: [
      {
        key: "hero", label: "Hero", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "CaminoSapiens" },
          { key: "heading", label: "Heading", type: "text", def: "Art from the Long Walk" },
          { key: "body", label: "Intro line", type: "textarea", def: "We make hand-pulled photogravure etchings inspired by pilgrimage — and we can turn your own photograph into an original etching." },
          { key: "cta_primary_label", label: "Primary button text", type: "text", def: "Turn Your Photo Into an Etching" },
          { key: "cta_primary_link", label: "Primary button link", type: "link", def: "/custom-etching" },
          { key: "cta_secondary_label", label: "Secondary button text", type: "text", def: "Explore Original Etchings" },
          { key: "cta_secondary_link", label: "Secondary button link", type: "link", def: "/etchings" },
          { key: "image", label: "Background image", type: "image", def: HERO },
          { key: "image_alt", label: "Background image alt text", type: "text", def: "A pilgrim with a scallop-shell backpack walking a dirt path winding through rolling green fields on the Camino de Santiago" },
        ],
      },
      {
        key: "intro", label: "Studio intro", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "The Studio" },
          { key: "body", label: "Body", type: "textarea", def: "Every long walk teaches you to look closer. The rust on a balcony, the light on a field at four in the afternoon — ordinary things become the ones you remember. We turn these images into etchings, to help the memory last." },
          { key: "link_label", label: "Link text", type: "text", def: "Read our story" },
          { key: "link_link", label: "Link", type: "link", def: "/about" },
        ],
      },
      {
        key: "featured", label: "Featured etchings", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Selected Works" },
          { key: "heading", label: "Heading", type: "text", def: "Featured Etchings" },
          { key: "view_all_label", label: "\"View all\" link text", type: "text", def: "View all" },
        ],
      },
      {
        key: "custom", label: "Custom etching feature", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Custom Etching" },
          { key: "heading", label: "Heading (use a new line for the break)", type: "textarea", def: "Your Photograph,\nMade Permanent" },
          { key: "body", label: "Body", type: "textarea", def: "Send us a photograph — a moment from your own walk, a face that matters, a place you can't forget — and we'll turn it into a hand-pulled photogravure. The feeling, made permanent." },
          {
            key: "steps", label: "Steps (icons are fixed)", type: "list", fixed: true,
            item: [{ key: "label", label: "Label", type: "text" }],
            def: [{ label: "Your Photograph" }, { label: "Etching Plate" }, { label: "Ink & Press" }, { label: "Finished Print" }],
          },
          { key: "cta_label", label: "Button text", type: "text", def: "Create an Etching" },
          { key: "cta_link", label: "Button link", type: "link", def: "/custom-etching" },
          { key: "image", label: "Image", type: "image", def: CUSTOM_IMG },
          { key: "image_alt", label: "Image alt text", type: "text", def: "A hand working ink into an etching plate at the press" },
        ],
      },
      {
        key: "process", label: "From photograph to print", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "The Making" },
          { key: "heading", label: "Heading", type: "text", def: "From Photograph to Print" },
          {
            key: "cards", label: "Cards", type: "list",
            item: [
              { key: "image", label: "Image", type: "image" },
              { key: "label", label: "Label", type: "text" },
              { key: "caption", label: "Caption", type: "text" },
            ],
            def: [
              { image: "https://images.unsplash.com/photo-1690265632035-b4defdd9e593?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", label: "The photograph", caption: "It begins with a single image from the road." },
              { image: "https://images.unsplash.com/photo-1643216674491-33878507b402?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", label: "The plate", caption: "The image is exposed onto a light-sensitive plate." },
              { image: "https://images.pexels.com/photos/6620993/pexels-photo-6620993.jpeg?auto=compress&cs=tinysrgb&w=900", label: "The ink", caption: "Ink is worked into the plate, then wiped away by hand." },
              { image: "https://images.pexels.com/photos/29396288/pexels-photo-29396288.jpeg?auto=compress&cs=tinysrgb&w=900", label: "The press", caption: "Plate and damp paper meet under the press." },
            ],
          },
          { key: "see_full_label", label: "\"See full process\" link text", type: "text", def: "See the full process" },
          { key: "see_full_link", label: "\"See full process\" link", type: "link", def: "/how-we-make-an-etching" },
        ],
      },
      {
        key: "stories", label: "Stories teaser", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "From the Road" },
          { key: "heading", label: "Heading", type: "text", def: "Stories" },
          { key: "view_all_label", label: "\"All stories\" link text", type: "text", def: "All stories" },
        ],
      },
      {
        key: "routes", label: "Routes teaser", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Walk With Us" },
          { key: "heading", label: "Heading", type: "text", def: "Explore by Route" },
          { key: "view_all_label", label: "\"All routes\" link text", type: "text", def: "All routes" },
        ],
      },
      {
        key: "photography", label: "Photography teaser", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Our Favorite Photographs" },
          { key: "heading", label: "Heading", type: "text", def: "Photography" },
          { key: "view_all_label", label: "\"Enjoy our photos\" link text", type: "text", def: "Enjoy our photos" },
        ],
      },
    ],
  },

  {
    key: "how_we_make", label: "How We Make an Etching",
    sections: [
      {
        key: "intro", label: "Intro", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "The Craft" },
          { key: "heading", label: "Heading", type: "text", def: "How We Make an Etching" },
          { key: "body", label: "Body", type: "textarea", def: "Photogravure is a slow, physical process. Here is how a photograph becomes a hand-pulled print." },
        ],
      },
      {
        key: "steps", label: "Process steps", fields: [
          {
            key: "items", label: "Steps", type: "list",
            item: [
              { key: "n", label: "Number", type: "text" },
              { key: "img", label: "Photo", type: "image" },
              { key: "alt", label: "Photo alt text", type: "text" },
              { key: "t", label: "Title", type: "text" },
              { key: "d", label: "Description", type: "textarea" },
            ],
            def: [
              { n: "01", t: "Select the photograph", d: "Everything begins with a single image — a moment worth keeping.", img: "https://images.unsplash.com/photo-1690265632035-b4defdd9e593?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Select the photograph" },
              { n: "02", t: "Prepare the image", d: "The photograph is adjusted for tone and contrast so it will translate to the plate.", img: "https://images.unsplash.com/photo-1544013679-25117c6fab34?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Prepare the image" },
              { n: "03", t: "Create the photopolymer plate", d: "The image is exposed onto a light-sensitive plate, hardening it into fine relief.", img: "https://images.unsplash.com/photo-1643216674491-33878507b402?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Create the photopolymer plate" },
              { n: "04", t: "Ink the plate", d: "Archival copperplate ink is worked into every recess of the plate by hand.", img: "https://images.pexels.com/photos/6620993/pexels-photo-6620993.jpeg?auto=compress&cs=tinysrgb&w=1000", alt: "Ink the plate" },
              { n: "05", t: "Wipe the plate by hand", d: "The surface is wiped clean so ink remains only where the image lives.", img: "https://images.unsplash.com/photo-1643216674491-33878507b402?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Wipe the plate by hand" },
              { n: "06", t: "Prepare damp fine-art paper", d: "Cotton-rag paper is dampened so it can receive the finest detail.", img: "https://images.unsplash.com/photo-1586939735437-d61392bd039b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Prepare damp fine-art paper" },
              { n: "07", t: "Run through the press", d: "Plate and paper meet under great pressure, transferring the image.", img: "https://images.pexels.com/photos/6620992/pexels-photo-6620992.jpeg?auto=compress&cs=tinysrgb&w=1000", alt: "Run through the press" },
              { n: "08", t: "Lift the finished print", d: "The paper is peeled back to reveal the image — the first look, every time a small event.", img: "https://images.pexels.com/photos/29396288/pexels-photo-29396288.jpeg?auto=compress&cs=tinysrgb&w=1000", alt: "Lift the finished print" },
              { n: "09", t: "Dry, inspect, sign & number", d: "Each print is dried flat, examined, then signed and numbered by the artist.", img: "https://images.unsplash.com/photo-1734089915366-facc08bcdcb6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Dry, inspect, sign and number" },
            ],
          },
        ],
      },
      {
        key: "cta", label: "Closing call to action", fields: [
          { key: "heading", label: "Heading", type: "text", def: "Ready to make your own?" },
          { key: "body", label: "Body", type: "textarea", def: "Turn a photograph that matters to you into a hand-pulled etching." },
          { key: "cta_label", label: "Button text", type: "text", def: "Create an Etching" },
          { key: "cta_link", label: "Button link", type: "link", def: "/custom-etching" },
        ],
      },
    ],
  },

  {
    key: "testimonials", label: "Testimonials",
    sections: [
      {
        key: "main", label: "Where Our Etchings Have Found a Home", fields: [
          { key: "eyebrow", label: "Eyebrow / locations line", type: "text", def: "Buenos Aires · Moscow · Perth · Madison" },
          { key: "heading", label: "Heading", type: "text", def: "Where Our Etchings Have Found a Home" },
          { key: "body", label: "Intro", type: "textarea", def: "Photographs and memories from people's journeys become handmade etchings in our Utrecht studio — and then find homes around the world. These are unsolicited words from a few of the people who received them." },
          {
            key: "items", label: "Testimonials", type: "list",
            item: [
              { key: "first_name", label: "First name", type: "text" },
              { key: "last_initial", label: "Last initial (e.g. L.)", type: "text" },
              { key: "location", label: "Location (City, Country)", type: "text" },
              { key: "quote", label: "Primary quote", type: "textarea" },
              { key: "quote2", label: "Second quote (optional)", type: "textarea" },
              { key: "photo", label: "Recipient photo (optional)", type: "image" },
              { key: "photo_alt", label: "Photo alt text (optional)", type: "text" },
              { key: "featured", label: "Feature this one large? (type: yes)", type: "text" },
              { key: "hidden", label: "Hide from site? (type: yes)", type: "text" },
            ],
            def: [
              { first_name: "Peter", last_initial: "L.", location: "Buenos Aires, Argentina", quote: "I happy as dog with two tails as we say in Argentina (contento como perro con dos colas)!!!!", quote2: "The etching you so kindly sent me has taken residence over my night table!", photo: "", photo_alt: "", featured: "yes", hidden: "" },
              { first_name: "Maria", last_initial: "I.", location: "Moscow, Russia", quote: "The picture is beautiful! I cannot wait to give it a nice glass frame and place on a wall.", quote2: "", photo: "", photo_alt: "", featured: "", hidden: "" },
              { first_name: "Catherine", last_initial: "C.", location: "Perth, Australia", quote: "I received the most beautiful piece of art in the mail. How on earth did you do that? I love it ❤️❤️ I am going to get it framed. It is so special, thank you 🙏", quote2: "", photo: "", photo_alt: "", featured: "", hidden: "" },
              { first_name: "Lisa", last_initial: "G.", location: "Madison, Indiana, USA", quote: "Ann and Eric, your thoughtful etching has found its home. Thank you!", quote2: "", photo: "", photo_alt: "", featured: "", hidden: "" },
            ],
          },
        ],
      },
    ],
  },


  {
    key: "custom_etching", label: "Custom Etching",
    sections: [
      {
        key: "header", label: "Header", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Custom Etching" },
          { key: "heading", label: "Heading", type: "text", def: "Turn Your Photograph Into an Etching" },
          { key: "body", label: "Body", type: "textarea", def: "Send us a photograph — a moment from your own walk, a face that matters, a place you can't forget — and we'll turn it into a hand-pulled photogravure etching. The feeling, made permanent." },
        ],
      },
      {
        key: "steps", label: "Steps (icons are fixed)", fields: [
          {
            key: "items", label: "Steps", type: "list", fixed: true,
            item: [
              { key: "t", label: "Title", type: "text" },
              { key: "d", label: "Description", type: "text" },
            ],
            def: [
              { t: "Your Photograph", d: "You upload an image that matters." },
              { t: "Etching Plate", d: "We prepare a photopolymer plate from it." },
              { t: "Ink & Press", d: "The plate is inked, wiped, and pressed by hand." },
              { t: "Finished Print", d: "A signed, one-of-a-kind photogravure arrives." },
            ],
          },
        ],
      },
      {
        key: "upload", label: "Upload area", fields: [
          { key: "eyebrow", label: "Step eyebrow", type: "text", def: "1 · Your Photograph" },
          { key: "button_text", label: "Upload button text", type: "text", def: "Click to upload your photograph" },
          { key: "button_hint", label: "Upload button hint", type: "text", def: "JPG, PNG, HEIC or TIFF · up to 25MB" },
          { key: "guidance", label: "Guidance note", type: "textarea", def: "For the best result, upload the highest-resolution version you have. Sharp, well-lit images with clear contrast translate beautifully. Phone photos are welcome." },
          { key: "review_note", label: "Review note", type: "textarea", def: "Our studio will review your photograph before beginning work and may contact you if the image is unsuitable for etching. Nothing is printed until we're both happy." },
        ],
      },
      {
        key: "options", label: "Options & pricing labels", fields: [
          { key: "size_eyebrow", label: "Size eyebrow", type: "text", def: "Size" },
          { key: "additional_eyebrow", label: "Additional prints eyebrow", type: "text", def: "Additional Prints" },
          { key: "additional_desc", label: "Additional prints note ({price} = per-print price)", type: "textarea", def: "Your commission includes the first print. Add extra prints of the same plate at {price} each." },
          { key: "notes_eyebrow", label: "Notes eyebrow", type: "text", def: "Notes for the Studio" },
          { key: "notes_placeholder", label: "Notes placeholder", type: "text", def: "Anything we should know about this image, or how you'd like it treated?" },
          { key: "line_item_label", label: "Line item label", type: "text", def: "Custom photogravure etching (incl. first print)" },
          { key: "total_label", label: "Total label", type: "text", def: "Total" },
          { key: "add_to_cart_label", label: "Add to cart button", type: "text", def: "Add to Cart" },
          { key: "included_note", label: "Included note", type: "text", def: "Plate creation and the first hand-pulled print are included." },
        ],
      },
      {
        key: "details", label: "Details section", fields: [
          {
            key: "items", label: "Detail blocks", type: "list",
            item: [
              { key: "eyebrow", label: "Eyebrow", type: "text" },
              { key: "body", label: "Body", type: "rich" },
            ],
            def: [
              { eyebrow: "Lead time", body: "<p>Because each etching is made by hand, please allow several weeks from approval to shipping. We'll keep you posted at each stage.</p>" },
              { eyebrow: "Paper & ink", body: "<p>Prints are pulled on heavyweight Hahnemühle Copperplate 300 gsm cotton-rag fine-art paper with archival intaglio oil-based ink.</p>" },
              { eyebrow: "Handmade variation", body: "<p>No two hand-pulled prints are identical. Small, natural variations in tone and plate texture are part of the character of an original etching.</p>" },
              { eyebrow: "Editions", body: "<p>Custom etchings are made from your own image and are not part of a numbered public edition.</p>" },
            ],
          },
        ],
      },
    ],
  },

  {
    key: "about", label: "About",
    sections: [
      {
        key: "header", label: "Header", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "About CaminoSapiens" },
          { key: "heading", label: "Heading", type: "text", def: "A Small Studio for the Long Walk" },
          { key: "image", label: "Portrait image", type: "image", def: PORTRAIT },
          { key: "image_alt", label: "Portrait alt text", type: "text", def: "On the road along the Camino" },
          { key: "body", label: "Story", type: "rich", def: "<p>Ann and Eric — two people who have spent years on the Camino de Santiago and other long roads, learning that walking, done slowly enough, becomes a way of seeing.</p><p>Rust blooming across a balcony rail. The soft shoulder of a distant hill. The particular gold that falls across a field at four in the afternoon. A long walk rearranges what you notice — the ordinary starts to matter, starts to stay with you.</p><p>We photograph these moments as they find us. Then we choose a few to carry further — into copper, acid, ink, and the press — as hand-pulled photogravure etchings.</p><p>Nothing is mass-produced. Nothing repeats exactly. Every print is pulled one impression at a time, on fine-art paper, by hand.</p>" },
        ],
      },
      {
        key: "values", label: "Values row", fields: [
          {
            key: "items", label: "Values", type: "list",
            item: [
              { key: "t", label: "Title", type: "text" },
              { key: "d", label: "Description", type: "text" },
            ],
            def: [
              { t: "Walking", d: "The route sets the pace and decides what we see." },
              { t: "Photography", d: "We gather images the way one gathers stones from a path." },
              { t: "Printmaking", d: "A few of those images become permanent, made by hand." },
            ],
          },
          { key: "cta_label", label: "Button text", type: "text", def: "Commission an Etching" },
          { key: "cta_link", label: "Button link", type: "link", def: "/custom-etching" },
        ],
      },
    ],
  },

  {
    key: "contact", label: "Contact",
    sections: [
      {
        key: "header", label: "Header", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Contact" },
          { key: "heading", label: "Heading", type: "text", def: "Say Hello" },
          { key: "body", label: "Body", type: "textarea", def: "Questions about an etching, a commission, shipping, or a photograph you'd like us to consider? Write to us — we read everything." },
        ],
      },
      {
        key: "form", label: "Form", fields: [
          { key: "name_placeholder", label: "Name placeholder", type: "text", def: "Your name" },
          { key: "email_placeholder", label: "Email placeholder", type: "text", def: "Your email" },
          { key: "message_placeholder", label: "Message placeholder", type: "text", def: "Your message" },
          { key: "submit_label", label: "Submit button", type: "text", def: "Send Message" },
          { key: "sending_label", label: "Submitting label", type: "text", def: "Sending…" },
        ],
      },
      {
        key: "success", label: "Success message", fields: [
          { key: "heading", label: "Heading", type: "text", def: "Thank you." },
          { key: "body", label: "Body", type: "textarea", def: "Your message has reached the studio. We'll be in touch soon." },
        ],
      },
    ],
  },

  {
    key: "etchings", label: "Original Etchings",
    sections: [
      {
        key: "header", label: "Header", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Original Etchings" },
          { key: "heading", label: "Heading", type: "text", def: "Etchings from the Camino" },
          { key: "body", label: "Body", type: "textarea", def: "Each work is a hand-pulled photogravure, drawn from moments of our years on the road. Signed, numbered, and made one at a time." },
        ],
      },
      {
        key: "ui", label: "Labels", fields: [
          { key: "available_label", label: "\"Available\" filter", type: "text", def: "Available" },
          { key: "sort_label", label: "Sort label", type: "text", def: "Sort" },
          { key: "load_more_label", label: "Load more button", type: "text", def: "Load more" },
          { key: "empty_text", label: "Empty state", type: "text", def: "No etchings match this filter." },
          { key: "archive_link_label", label: "\"View archive\" link", type: "text", def: "View the archive of sold works →" },
        ],
      },
    ],
  },

  {
    key: "archive", label: "The Archive (sold works)",
    sections: [
      {
        key: "header", label: "Header", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "The Archive" },
          { key: "heading", label: "Heading", type: "text", def: "Works that have found their homes" },
          { key: "body", label: "Intro / nudge", type: "textarea", def: "Every original etching is a small, finite edition — and once the last impression is gone, it stays here in the archive. These pieces are no longer for sale, but each one began as a single photograph pressed by hand into fine paper. If one of them speaks to you, we can begin again from an image of your own." },
          { key: "cta_label", label: "Commission button text", type: "text", def: "Commission a custom etching" },
          { key: "empty_text", label: "Empty state (nothing sold yet)", type: "text", def: "No works have sold out yet — the full collection is still available." },
        ],
      },
      {
        key: "footer_cta", label: "Closing commission block", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "From your own photograph" },
          { key: "heading", label: "Heading", type: "text", def: "Not everything here has to stay out of reach" },
          { key: "body", label: "Body", type: "textarea", def: "A custom photogravure etching is made the same way as every piece in this archive — by hand, from a photograph that matters to you. A place from your own long walk, perhaps." },
          { key: "cta_label", label: "Button text", type: "text", def: "Start a commission" },
        ],
      },
    ],
  },

  {
    key: "photography", label: "Photography",
    sections: [
      {
        key: "header", label: "Header", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Photography" },
          { key: "heading", label: "Heading", type: "text", def: "Our Favorite Photographs" },
          { key: "body", label: "Body", type: "textarea", def: "We haven't made etchings of all our images — but you might enjoy them anyway. And if you see one that needs to become an etching, let us know." },
        ],
      },
      {
        key: "ui", label: "Labels", fields: [
          { key: "collections_label", label: "Collections label", type: "text", def: "Collections" },
          { key: "view_all_collections_label", label: "\"View all collections\" link", type: "text", def: "View all →" },
          { key: "route_region_label", label: "Route & Region label", type: "text", def: "Route & Region" },
          { key: "all_routes_label", label: "\"Explore all routes\" link", type: "text", def: "Explore all routes →" },
          { key: "sort_label", label: "Sort label", type: "text", def: "Sort" },
        ],
      },
    ],
  },

  {
    key: "stories", label: "Stories",
    sections: [
      {
        key: "header", label: "Header", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Stories" },
          { key: "heading", label: "Heading", type: "text", def: "Reflections from the Road" },
          { key: "body", label: "Body", type: "textarea", def: "On walking, waiting, noticing, and the lessons that show up on the trail and after." },
          { key: "read_more_label", label: "\"Read more\" link", type: "text", def: "Read more" },
        ],
      },
    ],
  },

  {
    key: "cart", label: "Cart",
    sections: [
      {
        key: "header", label: "Header", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Your Selection" },
          { key: "heading", label: "Heading", type: "text", def: "Cart" },
        ],
      },
      {
        key: "empty", label: "Empty state", fields: [
          { key: "text", label: "Empty message", type: "text", def: "Your cart is quiet for now." },
          { key: "browse_label", label: "Browse link", type: "text", def: "Browse etchings" },
          { key: "commission_label", label: "Commission link", type: "text", def: "Commission your own" },
        ],
      },
      {
        key: "summary", label: "Summary", fields: [
          { key: "subtotal_label", label: "Subtotal label", type: "text", def: "Subtotal" },
          { key: "shipping_note", label: "Shipping note", type: "text", def: "Shipping is chosen at checkout." },
          { key: "checkout_label", label: "Checkout button", type: "text", def: "Continue to Checkout" },
          { key: "thumb_fallback", label: "Photo thumbnail fallback text", type: "text", def: "Your photo" },
        ],
      },
    ],
  },

  {
    key: "checkout", label: "Checkout",
    sections: [
      {
        key: "header", label: "Header", fields: [
          { key: "eyebrow", label: "Eyebrow", type: "text", def: "Almost There" },
          { key: "heading", label: "Heading", type: "text", def: "Checkout" },
        ],
      },
      {
        key: "labels", label: "Labels", fields: [
          { key: "details_label", label: "Your details", type: "text", def: "Your Details" },
          { key: "name_placeholder", label: "Name placeholder", type: "text", def: "Full name" },
          { key: "email_placeholder", label: "Email placeholder", type: "text", def: "Email" },
          { key: "phone_placeholder", label: "Phone placeholder", type: "text", def: "Phone number (optional)" },
          { key: "address1_placeholder", label: "Address line 1 placeholder", type: "text", def: "Address line 1" },
          { key: "address2_placeholder", label: "Address line 2 placeholder", type: "text", def: "Address line 2 (optional)" },
          { key: "city_placeholder", label: "City placeholder", type: "text", def: "City" },
          { key: "state_placeholder", label: "State/Province placeholder", type: "text", def: "State / Province / Region" },
          { key: "postal_placeholder", label: "Postal code placeholder", type: "text", def: "Postal / ZIP code" },
          { key: "country_placeholder", label: "Country placeholder", type: "text", def: "Select country" },
          { key: "destination_label", label: "Destination", type: "text", def: "Destination" },
          { key: "fulfillment_label", label: "Fulfillment", type: "text", def: "Fulfillment" },
          { key: "summary_label", label: "Order summary", type: "text", def: "Order Summary" },
          { key: "shipping_label", label: "Shipping row", type: "text", def: "Shipping" },
          { key: "included_label", label: "Included label (when shipping is €0)", type: "text", def: "Included" },
          { key: "tax_label", label: "Tax line", type: "text", def: "Tax included" },
          { key: "total_label", label: "Total", type: "text", def: "Total" },
          { key: "pay_label", label: "Pay button", type: "text", def: "Pay Securely" },
          { key: "redirecting_label", label: "Redirecting label", type: "text", def: "Redirecting…" },
          { key: "secure_note", label: "Secure note", type: "text", def: "Secure payment via Stripe." },
          { key: "country_us", label: "Country: United States", type: "text", def: "United States" },
          { key: "country_eu", label: "Country: Europe", type: "text", def: "Europe" },
          { key: "country_other", label: "Country: Rest of World", type: "text", def: "Rest of World" },
        ],
      },
    ],
  },

  {
    key: "payment_success", label: "Order Confirmed",
    sections: [
      {
        key: "states", label: "Messages", fields: [
          { key: "checking", label: "Checking message", type: "text", def: "Confirming your payment…" },
          { key: "paid_eyebrow", label: "Confirmed eyebrow", type: "text", def: "Thank You" },
          { key: "paid_heading", label: "Confirmed heading", type: "text", def: "Your order is confirmed" },
          { key: "paid_body", label: "Confirmed body", type: "textarea", def: "A confirmation is on its way to your inbox. Each print is made by hand — if your order includes a custom etching, our studio will review your photograph and be in touch before beginning work." },
          { key: "paid_link_label", label: "Confirmed link text", type: "text", def: "Read a story while you wait" },
          { key: "paid_link_link", label: "Confirmed link", type: "link", def: "/stories" },
          { key: "pending_heading", label: "Pending heading", type: "text", def: "Payment is processing" },
          { key: "pending_body", label: "Pending body", type: "textarea", def: "This can take a moment. We'll email your confirmation shortly." },
          { key: "error_heading", label: "Error heading", type: "text", def: "Something went wrong" },
          { key: "error_body", label: "Error body", type: "text", def: "We couldn't confirm this payment." },
          { key: "error_link_label", label: "Error link text", type: "text", def: "Return to cart" },
        ],
      },
    ],
  },

  {
    key: "shipping", label: "Legal · Shipping",
    sections: [{ key: "main", label: "Page", fields: [
      { key: "title", label: "Title", type: "text", def: "Shipping" },
      { key: "body", label: "Body", type: "rich", def: `<p>Every print is made by hand, so orders ship after production is complete. We will email you at each stage.</p><p>For orders to the United States, two fulfilment methods are offered at checkout: U.S. Studio Delivery (carried to the U.S. on our next scheduled trip and mailed domestically) and Direct International Delivery (shipped directly from Europe). Shipping within Europe and to the rest of the world is included in the purchase price.</p><p><strong>Nederlands &middot; Verzending</strong></p><p>Elke afdruk wordt met de hand gemaakt, dus bestellingen worden verzonden nadat de productie is voltooid. Wij houden u bij elke stap per e-mail op de hoogte.</p><p>Voor bestellingen naar de Verenigde Staten worden bij het afrekenen twee bezorgmethoden aangeboden: U.S. Studio Delivery (wordt op onze eerstvolgende geplande reis meegenomen naar de VS en daar binnenlands verstuurd) en Direct International Delivery (wordt rechtstreeks vanuit Europa verzonden). Verzending binnen Europa en naar de rest van de wereld is inbegrepen in de aankoopprijs.</p>` },
    ] }],
  },
  {
    key: "returns", label: "Legal · Returns",
    sections: [{ key: "main", label: "Page", fields: [
      { key: "title", label: "Title", type: "text", def: "Returns & Withdrawal" },
      { key: "body", label: "Body", type: "rich", def: `<p><strong>Right of Withdrawal</strong></p><p>If you are a consumer and purchase a non-personalised product from CaminoSapiens through our online store, you have the right to withdraw from the purchase contract within 14 calendar days without giving any reason.</p><p>The withdrawal period expires 14 days after the day on which you, or a third party designated by you other than the carrier, takes physical possession of the product.</p><p>To exercise your right of withdrawal, you must inform us of your decision to withdraw by means of a clear statement, for example by email or by using the Model Withdrawal Form. Use of the Model Withdrawal Form is optional. Your withdrawal notice must be sent before the 14-day withdrawal period has expired.</p><p><strong>Contact details for withdrawal notices:</strong> CaminoSapiens, 32C Nieuwegracht, Utrecht, NL 3512LS. Email: <a href="mailto:caminosapiens@gmail.com">caminosapiens@gmail.com</a></p><p><strong>Returns</strong></p><p>After notifying us that you are exercising your right of withdrawal, you must return the product without undue delay and no later than 14 days after the date on which you notified us of your withdrawal. Products must be returned to: CaminoSapiens, 32C Nieuwegracht, Utrecht, NL 3512LS.</p><p>You are responsible for the direct cost of returning the product, including return postage. We recommend using a trackable and adequately insured shipping method, as you remain responsible for the return shipment until it is received by us.</p><p>You may inspect and handle the product only as necessary to establish its nature, characteristics and functioning. You may be liable for any reduction in value caused by handling beyond what would normally be permitted in a physical shop.</p><p><strong>Refunds</strong></p><p>If you validly exercise your right of withdrawal, we will refund all payments received from you for the returned product, including the cost of our least expensive standard delivery option offered at the time of purchase. Additional delivery costs resulting from your choice of a more expensive or expedited delivery method will not be refunded.</p><p>We will issue the refund without undue delay and no later than 14 days after receiving your withdrawal notice. We may withhold the refund until we have received the returned product or until you provide satisfactory evidence that you have sent it back, whichever occurs first. Unless otherwise agreed, the refund will be made using the same payment method used for the original transaction. You will not be charged a fee for the refund.</p><p><strong>Exception for Custom-Made or Personalised Products</strong></p><p>The statutory right of withdrawal does not apply to products made according to the consumer&rsquo;s specifications or clearly personalised for the consumer. This exception includes, where applicable, custom photogravure etchings, commissioned artwork, plates or prints produced from a photograph supplied or selected by the customer, personalised inscriptions, individually approved compositions, and other products created specifically for a particular customer.</p><p>Once production of such a custom-made or personalised product has begun, the order cannot be cancelled under the statutory right of withdrawal. This exception does not affect your statutory rights if the product is defective, damaged, incorrectly produced, or does not conform to the agreement.</p><p><strong>Nederlands &middot; Herroepingsrecht</strong></p><p>Wanneer u als consument via onze webwinkel een niet-gepersonaliseerd product van CaminoSapiens koopt, heeft u het recht om de koopovereenkomst binnen 14 kalenderdagen zonder opgave van redenen te herroepen.</p><p>De herroepingstermijn verstrijkt 14 dagen na de dag waarop u, of een door u aangewezen derde die niet de vervoerder is, het product fysiek in bezit heeft gekregen.</p><p>Om gebruik te maken van uw herroepingsrecht, moet u ons door middel van een ondubbelzinnige verklaring op de hoogte stellen van uw beslissing de overeenkomst te herroepen. U kunt dit bijvoorbeeld per e-mail doen of gebruikmaken van het Modelformulier voor herroeping. Het gebruik van het modelformulier is niet verplicht. Uw verklaring van herroeping moet worden verzonden voordat de herroepingstermijn van 14 dagen is verstreken.</p><p><strong>Contactgegevens voor herroepingen:</strong> CaminoSapiens, 32C Nieuwegracht, Utrecht, NL 3512LS. E-mail: <a href="mailto:caminosapiens@gmail.com">caminosapiens@gmail.com</a></p><p><strong>Retourneren</strong></p><p>Nadat u ons heeft meegedeeld dat u gebruikmaakt van uw herroepingsrecht, moet u het product zonder onnodige vertraging en uiterlijk binnen 14 dagen na uw herroepingsmelding aan ons terugzenden. Producten moeten worden teruggestuurd naar: CaminoSapiens, 32C Nieuwegracht, Utrecht, NL 3512LS.</p><p>De directe kosten van het terugzenden van het product, waaronder de retourportokosten, zijn voor uw rekening. Wij adviseren u een traceerbare en voldoende verzekerde verzendmethode te gebruiken. U blijft verantwoordelijk voor de retourzending totdat deze door ons is ontvangen.</p><p>U mag het product alleen inspecteren en hanteren voor zover dat noodzakelijk is om de aard, kenmerken en werking ervan vast te stellen. U bent aansprakelijk voor een eventuele waardevermindering die het gevolg is van gebruik of behandeling die verder gaat dan in een fysieke winkel redelijkerwijs zou zijn toegestaan.</p><p><strong>Terugbetaling</strong></p><p>Wanneer u op geldige wijze gebruikmaakt van uw herroepingsrecht, betalen wij alle van u ontvangen betalingen voor het geretourneerde product terug. Dit omvat de kosten van de goedkoopste standaardleveringsmethode die wij bij de aankoop hebben aangeboden. Extra bezorgkosten die het gevolg zijn van uw keuze voor een duurdere of versnelde verzendmethode worden niet terugbetaald.</p><p>Wij betalen het bedrag zonder onnodige vertraging en uiterlijk binnen 14 dagen na ontvangst van uw herroepingsmelding terug. Wij mogen wachten met terugbetalen totdat wij het product hebben ontvangen of totdat u voldoende bewijs heeft geleverd dat u het product heeft teruggezonden. Tenzij anders overeengekomen, gebruiken wij voor de terugbetaling hetzelfde betaalmiddel als waarmee de oorspronkelijke aankoop is betaald. Voor de terugbetaling worden geen kosten in rekening gebracht.</p><p><strong>Uitzondering voor Maatwerk en Gepersonaliseerde Producten</strong></p><p>Het wettelijke herroepingsrecht is niet van toepassing op producten die volgens specificaties van de consument zijn vervaardigd of die duidelijk voor de consument zijn gepersonaliseerd. Deze uitzondering omvat, voor zover van toepassing, op maat gemaakte fotogravure-etsen, kunstwerken in opdracht, platen of afdrukken die worden vervaardigd aan de hand van een door de klant aangeleverde of geselecteerde foto, persoonlijke inscripties, individueel goedgekeurde composities en andere producten die speciaal voor een bepaalde klant worden gemaakt.</p><p>Zodra met de productie van een dergelijk maatwerk- of gepersonaliseerd product is begonnen, kan de bestelling niet op grond van het wettelijke herroepingsrecht worden geannuleerd. Deze uitzondering doet geen afbreuk aan uw wettelijke rechten wanneer het product gebrekkig of beschadigd is, verkeerd is vervaardigd of niet aan de overeenkomst beantwoordt.</p>` },
    ] }],
  },
  {
    key: "terms", label: "Legal · Terms",
    sections: [{ key: "main", label: "Page", fields: [
      { key: "title", label: "Title", type: "text", def: "Terms & Conditions" },
      { key: "body", label: "Body", type: "rich", def: `<p><strong>Impressum / Legal Disclosure</strong></p><p><strong>Business Name / Bedrijfsnaam:</strong> CaminoSapiens</p><p><strong>Registered Address / Vestigingsadres:</strong> 32C Nieuwegracht, 3512LS Utrecht, Nederland (please send returns by post; no personal visits without appointment)</p><p><strong>Chamber of Commerce / KvK-nummer:</strong> 97888494</p><p><strong>VAT / BTW-nummer:</strong> NL005294569B08</p><p><strong>Email / E-mailadres:</strong> <a href="mailto:caminosapiens@gmail.com">caminosapiens@gmail.com</a></p><p><strong>Responsible for content / Verantwoordelijk voor de inhoud:</strong> CaminoSapiens</p><p><strong>Description of Service</strong></p><p>We sell photographic prints and hand-pulled photogravure etchings produced in our own studio, as well as a personalized etching service created in our studio.</p><p>Wij verkopen fotografische afdrukken en handgedrukte fotogravure-etsen die in ons eigen atelier worden vervaardigd, evenals een gepersonaliseerde etsendienst die in ons atelier wordt gemaakt.</p><p><strong>Disclaimer</strong></p><p>While we strive for accuracy, we accept no liability for the correctness or completeness of content presented on this website.</p><p>Hoewel wij uiterste zorg besteden aan de inhoud van deze website, kunnen wij geen aansprakelijkheid aanvaarden voor de juistheid of volledigheid van de verstrekte informatie.</p><p><strong>Terms &amp; Conditions</strong></p><p>By using this website, you agree to the following terms. We sell studio-produced photographic prints and etchings and a personalized etching service. Because many pieces are made by hand to order in our studio, delivery times may vary.</p><p><strong>Return Policy:</strong> Due to the custom nature of many of our products, the right of withdrawal may not apply in all cases. Please contact us if you have questions about returns.</p><p>Prices include VAT. Orders are produced in our studio and shipped using professional postal and courier services. We reserve the right to update prices, products, or terms at any time without prior notice.</p><p><strong>Algemene Voorwaarden</strong></p><p>Door gebruik te maken van deze website stemt u in met de onderstaande voorwaarden. Wij bieden in ons eigen atelier vervaardigde fotografische afdrukken en etsen aan, evenals een gepersonaliseerde etsendienst. Omdat veel werken met de hand en op bestelling in ons atelier worden gemaakt, kan de levertijd vari&euml;ren.</p><p><strong>Retourbeleid:</strong> Omdat veel van onze producten gepersonaliseerd of op bestelling gemaakt zijn, geldt het herroepingsrecht niet voor elk product. Neem contact op indien u vragen hebt over retourmogelijkheden.</p><p>Prijzen zijn inclusief btw. Bestellingen worden in ons atelier vervaardigd en verzonden met professionele post- en koeriersdiensten. Wij behouden ons het recht voor om prijzen, producten of voorwaarden te wijzigen zonder voorafgaande kennisgeving.</p>` },
    ] }],
  },
  {
    key: "privacy", label: "Legal · Privacy",
    sections: [{ key: "main", label: "Page", fields: [
      { key: "title", label: "Title", type: "text", def: "Privacy & Cookies" },
      { key: "body", label: "Body", type: "rich", def: `<p><strong>Privacy Policy</strong></p><p>We respect your privacy and handle your data with care. Your personal information is only used to process orders, improve our services, and contact you if needed.</p><p>We collect data such as your name, email, shipping address, and order history. This is used exclusively for order fulfillment, customer service, and (if applicable) marketing you have consented to.</p><p>Your data may be shared with payment processors and the postal/courier carriers we use to fulfill your order.</p><p>You have the right to access, correct, or delete your data. Contact us at <a href="mailto:caminosapiens@gmail.com">caminosapiens@gmail.com</a>.</p><p><strong>Cookie Policy</strong></p><p>This website uses cookies to enhance usability, analyze visitor behavior (e.g., via Google Analytics), and serve personalized content. By continuing to browse, you consent to the use of cookies.</p><p><strong>Nederlands &middot; Privacybeleid</strong></p><p>Wij respecteren uw privacy en behandelen uw gegevens met zorg. Uw persoonlijke gegevens worden uitsluitend gebruikt om bestellingen te verwerken, onze diensten te verbeteren en, indien gewenst, contact met u op te nemen.</p><p>Wij verzamelen gegevens zoals uw naam, e-mailadres, verzendadres en aankoopgeschiedenis. We gebruiken deze uitsluitend voor orderverwerking, klantenservice en (indien van toepassing) marketing waarvoor u toestemming hebt gegeven.</p><p>Gegevens worden gedeeld met betalingsverwerkers en de post- en koeriersdiensten die wij gebruiken om uw bestelling uit te voeren.</p><p>U hebt recht op inzage, correctie of verwijdering van uw gegevens. Neem contact met ons op via <a href="mailto:caminosapiens@gmail.com">caminosapiens@gmail.com</a>.</p><p><strong>Cookieverklaring</strong></p><p>Deze website maakt gebruik van cookies om het gebruiksgemak te verbeteren, bezoekersgedrag te analyseren (via bijvoorbeeld Google Analytics), en gepersonaliseerde inhoud aan te bieden. Door verder te surfen, gaat u akkoord met ons cookiegebruik.</p>` },
    ] }],
  },

  {
    key: "not_found", label: "404 Page",
    sections: [{ key: "main", label: "Content", fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text", def: "404" },
      { key: "heading", label: "Heading", type: "text", def: "This path leads nowhere" },
      { key: "body", label: "Body", type: "text", def: "Every walk has a wrong turn. Let's get you back on the road." },
      { key: "link_label", label: "Link text", type: "text", def: "Return home" },
    ] }],
  },

  {
    key: "nav", label: "Navigation",
    sections: [
      {
        key: "brand", label: "Logo", fields: [
          { key: "name", label: "Studio name", type: "text", def: "CaminoSapiens" },
          { key: "tagline", label: "Tagline", type: "text", def: "Art of the Long Walk" },
        ],
      },
      {
        key: "links", label: "Menu links (labels only — destinations locked)", fields: [
          {
            key: "items", label: "Links", type: "list", fixed: true, lockKeys: ["to"],
            item: [
              { key: "label", label: "Label", type: "text" },
              { key: "to", label: "Destination", type: "locked" },
            ],
            def: [
              { to: "/custom-etching", label: "Custom Etching" },
              { to: "/etchings", label: "Original Etchings" },
              { to: "/etchings/archive", label: "Archive" },
              { to: "/photography", label: "Photography" },
              { to: "/stories", label: "Stories" },
              { to: "/how-we-make-an-etching", label: "How We Make an Etching" },
              { to: "/about", label: "About" },
              { to: "/contact", label: "Contact" },
            ],
          },
        ],
      },
    ],
  },

  {
    key: "footer", label: "Footer",
    sections: [
      {
        key: "brand", label: "Brand", fields: [
          { key: "name", label: "Studio name", type: "text", def: "CaminoSapiens" },
          { key: "tagline", label: "Tagline", type: "text", def: "Art of the Long Walk" },
          { key: "description", label: "Description", type: "textarea", def: "A small working art studio making hand-pulled photogravure etchings from the long walk." },
        ],
      },
      {
        key: "explore", label: "Explore column (labels only — destinations locked)", fields: [
          { key: "heading", label: "Heading", type: "text", def: "Explore" },
          {
            key: "links", label: "Links", type: "list", fixed: true, lockKeys: ["to"],
            item: [
              { key: "label", label: "Label", type: "text" },
              { key: "to", label: "Destination", type: "locked" },
            ],
            def: [
              { to: "/etchings", label: "Original Etchings" },
              { to: "/custom-etching", label: "Custom Etching" },
              { to: "/photography", label: "Photography" },
              { to: "/stories", label: "Stories" },
              { to: "/how-we-make-an-etching", label: "How We Make an Etching" },
            ],
          },
        ],
      },
      {
        key: "information", label: "Information column (labels only — destinations locked)", fields: [
          { key: "heading", label: "Heading", type: "text", def: "Information" },
          {
            key: "links", label: "Links", type: "list", fixed: true, lockKeys: ["to"],
            item: [
              { key: "label", label: "Label", type: "text" },
              { key: "to", label: "Destination", type: "locked" },
            ],
            def: [
              { to: "/shipping", label: "Shipping · Verzending" },
              { to: "/returns", label: "Returns · Retourneren" },
              { to: "/terms", label: "Terms · Voorwaarden" },
              { to: "/privacy", label: "Privacy · Privacybeleid" },
              { to: "/contact", label: "Contact" },
            ],
          },
        ],
      },
      {
        key: "newsletter", label: "Newsletter", fields: [
          { key: "heading", label: "Heading", type: "text", def: "Newsletter" },
          { key: "body", label: "Body", type: "text", def: "Occasional letters from the road. No noise." },
          { key: "placeholder", label: "Email placeholder", type: "text", def: "Your email" },
          { key: "button_label", label: "Button label", type: "text", def: "Join" },
          { key: "instagram_url", label: "Instagram URL", type: "link", def: "https://instagram.com" },
          { key: "facebook_url", label: "Facebook URL", type: "link", def: "https://facebook.com" },
        ],
      },
      {
        key: "bottom", label: "Bottom bar", fields: [
          { key: "copyright", label: "Copyright (© and year added automatically)", type: "text", def: "CaminoSapiens. All rights reserved." },
          { key: "admin_label", label: "Studio login label", type: "text", def: "Studio Login" },
        ],
      },
    ],
  },
];

// Build the full DEFAULTS object { page: { section: { field: value } } } from the schema.
export function buildDefaults() {
  const out = {};
  for (const page of CONTENT_SCHEMA) {
    out[page.key] = {};
    for (const section of page.sections) {
      out[page.key][section.key] = {};
      for (const field of section.fields) {
        out[page.key][section.key][field.key] = field.type === "list"
          ? JSON.parse(JSON.stringify(field.def || []))
          : (field.def ?? "");
      }
    }
  }
  return out;
}

export const DEFAULTS = buildDefaults();

// ---------------------------------------------------------------------------
// Locale scaffolding. Content OVERRIDES are stored namespaced by locale
// (e.g. { en: {...}, nl: {...} }) so more languages can be added later WITHOUT
// changing the CMS, endpoints, or page components. Today English is the only
// active language and the default. To add a language later: add an entry here
// with active:true, add its translations in the editor, and add a site switcher.
// The schema `def` values above are the canonical English source and act as the
// fallback for any locale that has no override for a given field.
export const LOCALES = [
  { code: "en", label: "English", active: true },
  // Example (inactive placeholder — do NOT enable until translated):
  // { code: "nl", label: "Nederlands", active: false },
];
export const DEFAULT_LOCALE = "en";
export const ACTIVE_LOCALES = LOCALES.filter((l) => l.active);
const LOCALE_CODES = LOCALES.map((l) => l.code);

// Returns the override tree for a locale from a stored `data` object. Backward
// compatible: if `data` is the old flat (non-namespaced) shape, it is treated as
// the default-locale tree.
export function localeData(data, locale) {
  if (!data || typeof data !== "object") return {};
  const keys = Object.keys(data);
  const namespaced = keys.length === 0 || keys.some((k) => LOCALE_CODES.includes(k));
  return namespaced ? (data[locale] || {}) : data;
}

// Writes `tree` as the override for `locale` while preserving any other locales
// already present. Normalises a legacy flat object into { en: <flat> } first.
export function withLocale(data, locale, tree) {
  const keys = Object.keys(data || {});
  const namespaced = keys.length === 0 || keys.some((k) => LOCALE_CODES.includes(k));
  const base = namespaced ? { ...(data || {}) } : (keys.length ? { [DEFAULT_LOCALE]: data } : {});
  base[locale] = tree;
  return base;
}
